import { ButtonApiErrorWrapper } from 'app/components/shared/ButtonApiErrorWrapper'
import { BgShapes } from 'app/components/ui/BgShapes'
import { Button } from 'app/components/ui/Button'
import { Container } from 'app/components/ui/Container'
import { ErrorFormScrollHandler } from 'app/components/ui/ErrorFormScrollHandler'
import { Form } from 'app/components/ui/Form'
import { LoadingSpinner } from 'app/components/ui/LoadingSpinner'
import { Text } from 'app/components/ui/Text'
import { useAuth } from 'app/contexts/AuthenticationProvider'
import { useGetAllLocations } from 'app/queries/location'
import { useUpdateUser } from 'app/queries/user'
import { protectedRoutes } from 'app/routing/routes'
import { RegistrationFormSteps, Location } from 'EstraSponsor'
import React, { useState } from 'react'
import { useErrorHandler } from 'react-error-boundary'
import { useHistory } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'

import { adaptFormDataToAPI, adaptApiToFormData } from './adapters'
import { userRegistrationDefaultValues } from './defaultValues'
import {
  saveDraftToLocalStorage,
  deleteDraftFromLocalStorage,
  restoreDraftFromLocalStorage,
} from './draft'
import { UserRegistrationFormData } from './formTypes'
import { UserRegistrationErrorHandler } from './UserRegistrationErrorHandler'
import { AccountingDataInnerForm } from './UserRegistrationInnerForm/AccountingDataInnerForm'
import { UserDataInnerForm } from './UserRegistrationInnerForm/UserDataInnerForm'
import {
  getUserDataErrorsFromApiError,
  isSponsorUpdateApiError,
  isSponsorUpdateUserDataApiError,
  SponsorUpdateApiError,
} from './validation'

type Props = {
  activeStep: RegistrationFormSteps
  setActiveStep: (step: RegistrationFormSteps) => void
}

export const UserRegistrationForm = ({
  activeStep,
  setActiveStep,
}: Props): JSX.Element => {
  const theme = useTheme()
  const { push } = useHistory()

  const { user } = useAuth()

  const {
    data: locations,
    error: getAllLocationsError,
    isLoading: getAllLocationsIsLoading,
  } = useGetAllLocations()

  const { mutate: updateUser, isLoading, isError, error } = useUpdateUser()
  const [updateUserError, setUpdateUserError] =
    useState<SponsorUpdateApiError | null>(null)

  useErrorHandler(getAllLocationsError)

  if (getAllLocationsIsLoading) return <LoadingSpinner />

  const onSubmit = (data: UserRegistrationFormData) => {
    saveDraftToLocalStorage(data, user?.id as number)

    updateUser(adaptFormDataToAPI(data), {
      onSuccess: () => {
        if (activeStep === 'user_data') {
          setActiveStep('accounting_data')
          window.scrollTo(0, 0)
        } else {
          deleteDraftFromLocalStorage(user?.id as number)
          push(protectedRoutes.documentsRegistration.path)
        }
      },
      onError: (error) => {
        // Go to second step only if there are no errors on first step
        // and if the Api error is a validation error, not a generic one
        const errorData = error?.response?.data

        if (!errorData || !isSponsorUpdateApiError(errorData)) return

        if (activeStep === 'user_data') {
          if (!isSponsorUpdateUserDataApiError(errorData)) {
            setActiveStep('accounting_data')
            window.scrollTo(0, 0)
          }
          setUpdateUserError(getUserDataErrorsFromApiError(errorData))
        } else {
          setUpdateUserError(errorData)
        }
      },
    })
  }

  // If sponsee data exists (old sponsee invited or edit), use existing data as default values for form,
  // otherwise use the basic default values
  const defaultValues = user?.sponsorman
    ? adaptApiToFormData(user.sponsorman, userRegistrationDefaultValues)
    : userRegistrationDefaultValues

  // Now we merge the default values with the ones found in the local storage as draft
  const defaultValuesWithDraft = restoreDraftFromLocalStorage(
    user?.id as number,
    defaultValues
  )

  return (
    <Form<UserRegistrationFormData>
      defaultValues={defaultValuesWithDraft}
      onSubmit={onSubmit}
      revalidateOnChange={false}
      skipValidation
    >
      {updateUserError && (
        <>
          <UserRegistrationErrorHandler error={updateUserError} />
          <ErrorFormScrollHandler error={updateUserError} />
        </>
      )}
      <BgShapes bgColor={theme.color.neutri.grigioBg} variant='form-section'>
        <FormWrapper>
          {activeStep === 'user_data' ? (
            <UserDataInnerForm
              userEmail={user?.email as string}
              locations={locations as Location[]}
            ></UserDataInnerForm>
          ) : (
            <AccountingDataInnerForm
              locations={locations as Location[]}
            ></AccountingDataInnerForm>
          )}
        </FormWrapper>
      </BgShapes>
      <ActionContainer>
        <ButtonApiErrorWrapper
          isError={isError && !updateUserError}
          error={error}
          alignCenter
        >
          <Text variant='s24' mb={3}>
            A questo punto, se hai compilato TUTTI i campi (quelli che competono
            alla tua condizione anagrafica societaria), salva i dati cliccando
            sul pulsante AVANTI qui sotto, procederai alla pagina successiva,
            altrimenti salverai il modulo in bozza e potrai terminare la
            compilazione più tardi.
          </Text>
          <ButtonsWrapper>
            {activeStep === 'accounting_data' && (
              <Button
                size='lg'
                variant='grey'
                onClick={() => {
                  setActiveStep('user_data')
                  window.scrollTo(0, 0)
                }}
                disabled={isLoading}
              >
                INDIETRO
              </Button>
            )}
            <Button size='lg' type='submit' disabled={isLoading}>
              {isLoading ? 'Caricamento...' : 'AVANTI'}
            </Button>
          </ButtonsWrapper>
        </ButtonApiErrorWrapper>
      </ActionContainer>
    </Form>
  )
}

const FormWrapper = styled.div`
  padding-top: 2rem;
`

const ActionContainer = styled(Container)`
  margin-top: 2rem;
`

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  button {
    margin: 0 0 2rem 0;
  }

  @media (min-width: ${({ theme }) => theme.bp.tablet}) {
    flex-direction: row;

    button {
      margin: 0 1rem 0 1rem;
    }
  }
`
